import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  writeBatch,
  runTransaction,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import {
  StudentSubmission,
  ReflectionGameState,
  Campaign,
  BlockAAnswer,
  CampaignAnswer,
  LevelProgress,
  LevelState
} from '../types';
import { getCurriculumAdapterById } from '../../curriculum/adapters/registry';
import { SeasonPassService } from './SeasonPassService';
import { DEFAULT_SEASON } from '../data/seasonPassData';

// Utility to clean undefined values from objects before Firestore writes
function cleanForFirestore(value: any): any {
  if (Array.isArray(value)) {
    return value.map(cleanForFirestore).filter(x => x !== undefined);
  }
  if (value && typeof value === 'object' && value.constructor === Object) {
    const cleaned: any = {};
    for (const [key, val] of Object.entries(value)) {
      const cleanedVal = cleanForFirestore(val);
      if (cleanedVal !== undefined) {
        cleaned[key] = cleanedVal;
      }
    }
    return cleaned;
  }
  return value === undefined ? undefined : value;
}

export class ProgressService {

  // Initialize default reflection game state for new users
  static getDefaultReflectionState(): ReflectionGameState {
    return {
      completedLevels: {},
      unlockedCount: 0,
      stats: {
        totalQuestionsAnswered: 0,
        totalCashFromProgress: 0
      }
    };
  }

  // Ensure user has a valid reflection game state document
  static async ensureReflectionStateExists(studentId: string): Promise<void> {
    try {
      const reflectionGameRef = doc(db, 'users', studentId, 'reflectionGame', 'state');
      const reflectionGameDoc = await getDoc(reflectionGameRef);

      if (!reflectionGameDoc.exists()) {
        console.log('[ProgressService.ensureReflectionStateExists] Creating initial state for user', { studentId });
        await setDoc(reflectionGameRef, this.getDefaultReflectionState());
      } else {
        // Validate existing state and fix if corrupted
        const existingData = reflectionGameDoc.data();
        if (!existingData || typeof existingData !== 'object' ||
            !existingData.completedLevels || typeof existingData.completedLevels !== 'object') {
          console.warn('[ProgressService.ensureReflectionStateExists] Fixing corrupted state for user', { studentId });
          await setDoc(reflectionGameRef, this.getDefaultReflectionState(), { merge: true });
        }
      }
    } catch (error) {
      console.error('Error ensuring reflection state exists:', error);
    }
  }

  // Get student's active squad with live session
  static async getActiveSquad(studentId: string): Promise<{ squadId: string; sessionId: string } | null> {
    try {
      // Find squads where student is a member
      const squadsRef = collection(db, 'squads');
      const q = query(squadsRef, where('studentIds', 'array-contains', studentId));
      const snapshot = await getDocs(q);
      
      const activeSquads = [];
      
      for (const squadDoc of snapshot.docs) {
        const squadData = squadDoc.data();
        if (squadData.liveSession && squadData.liveSession.sessionId) {
          activeSquads.push({
            squadId: squadDoc.id,
            sessionId: squadData.liveSession.sessionId,
            startTime: squadData.liveSession.startTime
          });
        }
      }
      
      if (activeSquads.length === 0) return null;
      
      // Return the most recently started session
      activeSquads.sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
      return {
        squadId: activeSquads[0].squadId,
        sessionId: activeSquads[0].sessionId
      };
    } catch (error) {
      console.error('Error getting active squad:', error);
      return null;
    }
  }

  // Get level progression for student
  static async getLevelProgress(studentId: string, squadId: string): Promise<LevelProgress[]> {
    try {
      // Get squad's current lesson ID
      const squadDoc = await getDoc(doc(db, 'squads', squadId));
      const squadData = squadDoc.data();
      const currentLessonId = squadData?.currentLessonId || '1';
      const curriculumId = (squadData?.curriculumId || 'music') as string;
      const adapter = getCurriculumAdapterById(curriculumId);

      // Ensure reflection state exists for this user before accessing it
      await this.ensureReflectionStateExists(studentId);

      // Get student's completed levels scoped by squad and curriculum, with legacy fallback
      const reflectionGameDoc = await getDoc(doc(db, 'users', studentId, 'reflectionGame', 'state'));
      let completedLevels: Record<string, any> = {};
      if (reflectionGameDoc.exists()) {
        const reflectionData = reflectionGameDoc.data() as any;
        const bySC = reflectionData?.completedLevelsBySquadCurriculum?.[squadId]?.[curriculumId];
        if (bySC && typeof bySC === 'object') {
          completedLevels = bySC;
        } else if (reflectionData?.completedLevels && typeof reflectionData.completedLevels === 'object') {
          // Legacy fallback
          completedLevels = reflectionData.completedLevels;
        } else {
          console.debug('[ProgressService.getLevelProgress] No completedLevels (scoped or legacy) found for user', { studentId, squadId, curriculumId });
        }
      } else {
        console.debug('[ProgressService.getLevelProgress] No reflection game state found for user', { studentId });
      }

      const currentIndex = adapter.getLevelIndex(currentLessonId);
      return adapter.levels.map(level => {
        const levelIndex = adapter.getLevelIndex(level.id);
        const isCompleted = completedLevels[level.id];

        let state: LevelState;
        if (isCompleted) {
          state = 'completed';
        } else if (levelIndex <= currentIndex) {
          state = 'ready';
        } else {
          state = 'locked';
        }

        return {
          id: level.id,
          title: level.title,
          state,
          completedAt: isCompleted?.submittedAt || undefined
        };
      });
    } catch (error) {
      console.error('Error getting level progress:', error);
      return [];
    }
  }

  // Get eligible campaigns for student
  static async getEligibleCampaigns(studentId: string, squadId: string): Promise<Campaign[]> {
    try {
      // const now = Timestamp.now(); // TODO: Add date filtering when needed
      
      // Get active campaigns for this squad
      const campaignsRef = collection(db, 'campaigns');
      const q = query(
        campaignsRef, 
        where('active', '==', true),
        where('squadIds', 'array-contains', squadId)
      );
      
      const snapshot = await getDocs(q);
      const eligibleCampaigns: Campaign[] = [];
      
      for (const campaignDoc of snapshot.docs) {
        const campaignData = { id: campaignDoc.id, ...campaignDoc.data() } as Campaign;
        
        // Filter by date range in memory
        const now = Timestamp.now();
        if (campaignData.dateRange.start.toMillis() > now.toMillis() || 
            campaignData.dateRange.end.toMillis() < now.toMillis()) {
          continue;
        }
        
        // Check if student has already answered this campaign
        const campaignStateDocRef = doc(db, 'users', studentId, 'campaignState', campaignDoc.id);
        const campaignStateDoc = await getDoc(campaignStateDocRef);
        
        const hasAnswered = campaignStateDoc.exists() && campaignStateDoc.data()?.answered;
        
        if (!hasAnswered) {
          eligibleCampaigns.push(campaignData);
          
          // Mark as first assigned if not already done
          if (!campaignStateDoc.exists()) {
            await setDoc(campaignStateDocRef, {
              answered: false,
              firstAssignedAt: serverTimestamp()
            });
          }
        }
      }
      
      return eligibleCampaigns;
    } catch (error) {
      console.error('Error getting eligible campaigns:', error);
      return [];
    }
  }

  // Create idempotent cash event and update wallet
  static async awardMuseCash(
    studentId: string, 
    amount: number, 
    sessionId: string, 
    levelId: string, 
    questionId: string
  ): Promise<boolean> {
    try {
      console.log('[ProgressService.awardMuseCash] start', { studentId, amount, sessionId, levelId, questionId });
      const cashEventId = `${sessionId}:${studentId}:${levelId}:${questionId}`;
      
      const result = await runTransaction(db, async (transaction) => {
        const cashEventRef = doc(db, 'users', studentId, 'cashEvents', cashEventId);
        const userRef = doc(db, 'users', studentId);
        
        // Transaction refs prepared
        
        // Check if cash event already exists
        const cashEventDoc = await transaction.get(cashEventRef);
        if (cashEventDoc.exists()) {
          console.log('[ProgressService.awardMuseCash] already exists', { cashEventId });
          return false; // Already rewarded
        }
        
        // Get current user data
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          console.error('[ProgressService.awardMuseCash] user not found', { studentId });
          throw new Error('User not found');
        }
        
        const userData = userDoc.data();
        const currentCash = userData.museCash || 0;
        const newCash = currentCash + amount;
        
        // Create cash event
        transaction.set(cashEventRef, {
          amount,
          source: 'progress',
          sessionId,
          levelId,
          questionId,
          createdAt: serverTimestamp()
        });
        
        // Update user's muse cash
        transaction.update(userRef, {
          museCash: newCash
        });
        return true;
      });
      console.log('[ProgressService.awardMuseCash] success', { result, cashEventId: `${sessionId}:${studentId}:${levelId}:${questionId}` });
      return result;
    } catch (error) {
      console.error('Error awarding muse cash:', error);
      return false;
    }
  }

  // Submit student feedback and award cash
  static async submitReflection(
    studentId: string,
    squadId: string,
    sessionId: string,
    levelId: string,
    blockAAnswers: Record<string, BlockAAnswer>,
    blockBAnswers: CampaignAnswer[]
  ): Promise<{ success: boolean; totalAwarded: number; submissionId?: string }> {
    try {
      console.log('[ProgressService.submitReflection] start', {
        studentId,
        squadId,
        sessionId,
        levelId,
        blockAKeys: Object.keys(blockAAnswers),
        blockBCount: blockBAnswers.length
      });
      const submissionId = `${sessionId}_${studentId}_${levelId}`;
      const submissionRef = doc(db, 'studentFeedback', submissionId);
      // Check if already submitted
      const existingSubmission = await getDoc(submissionRef);
      if (existingSubmission.exists()) {
        console.warn('[ProgressService.submitReflection] duplicate submission, ignoring', { submissionId });
        return { success: false, totalAwarded: 0 };
      }
      
      let totalAwarded = 0;
      const cashEventIds: string[] = [];
      
      // Award cash for Block A questions - only for complete answers
      const blockAQuestionIds = Object.keys(blockAAnswers);
      console.log('[ProgressService.submitReflection] awarding block A', { count: blockAQuestionIds.length });
      for (const questionId of blockAQuestionIds) {
        // Safety check: only award cash if questionId exists and answer is complete
        const answer = blockAAnswers[questionId];
        if (!questionId || !answer?.questionId) {
          // Skip incomplete Block A answers
          console.warn('[ProgressService.submitReflection] skipping incomplete blockA answer', { questionId, answer });
          continue;
        }
        
        const awarded = await this.awardMuseCash(studentId, 20, sessionId, levelId, questionId);
        if (awarded) {
          totalAwarded += 20;
          cashEventIds.push(`${sessionId}:${studentId}:${levelId}:${questionId}`);
        }
      }
      
      // Award cash for Block B questions - only for complete answers
      console.log('[ProgressService.submitReflection] awarding block B', { count: blockBAnswers.length });
      for (const answer of blockBAnswers) {
        // Safety check: only award cash if answer is complete
        if (!answer?.campaignId || !answer?.questionId) {
          // Skip incomplete Block B answers
          console.warn('[ProgressService.submitReflection] skipping incomplete blockB answer', { answer });
          continue;
        }
        
        const questionId = `${answer.campaignId}-${answer.questionId}`;
        const awarded = await this.awardMuseCash(studentId, 20, sessionId, levelId, questionId);
        if (awarded) {
          totalAwarded += 20;
          cashEventIds.push(`${sessionId}:${studentId}:${levelId}:${questionId}`);
        }
      }
      
      // Build submission data

      // Create submission record
      const submission: StudentSubmission = {
        studentId,
        squadId,
        sessionId,
        levelId,
        submittedAt: Timestamp.now(),
        blockA: blockAAnswers,
        blockB: blockBAnswers,
        awards: {
          totalQuestions: blockAQuestionIds.length + blockBAnswers.length,
          totalMuseCash: totalAwarded,
          cashEventIds
        },
        templateSnapshot: {
          levelId,
          campaignIds: [...new Set(blockBAnswers.map(a => a.campaignId).filter(id => id))]
        }
      };
      
      // Clean submission object to remove any undefined values
      const cleanSubmission = cleanForFirestore(submission);
      
      // Save submission and update progress state
      const batch = writeBatch(db);
      
      console.log('[ProgressService.submitReflection] writing submission + progress updates', { submissionId, totalAwarded });
      batch.set(submissionRef, cleanSubmission);
      
      // Update reflection game state
      const reflectionGameRef = doc(db, 'users', studentId, 'reflectionGame', 'state');
      const reflectionGameDoc = await getDoc(reflectionGameRef);
      const currentState = reflectionGameDoc.exists() ? reflectionGameDoc.data() as ReflectionGameState : {
        completedLevels: {},
        unlockedCount: 0,
        stats: { totalQuestionsAnswered: 0, totalCashFromProgress: 0 }
      };

      // Defensive defaults: some legacy accounts may be missing these maps
      if (!currentState.completedLevels || typeof (currentState as any).completedLevels !== 'object') {
        (currentState as any).completedLevels = {};
      }
      if (!currentState.stats) {
        (currentState as any).stats = { totalQuestionsAnswered: 0, totalCashFromProgress: 0 } as any;
      } else {
        (currentState as any).stats.totalQuestionsAnswered = (currentState as any).stats.totalQuestionsAnswered || 0;
        (currentState as any).stats.totalCashFromProgress = (currentState as any).stats.totalCashFromProgress || 0;
      }

      // Keep legacy flat map for backward compatibility
      currentState.completedLevels[levelId] = {
        submissionId,
        submittedAt: Timestamp.now()
      } as any;
      // New: scoped map by squad and curriculum
      const squadSnap = await getDoc(doc(db, 'squads', squadId));
      const curriculumId = (squadSnap.data()?.curriculumId || 'music') as string;
      const scopedPath = `completedLevelsBySquadCurriculum.${squadId}.${curriculumId}.${levelId}`;
      currentState.stats.totalQuestionsAnswered += submission.awards.totalQuestions;
      currentState.stats.totalCashFromProgress += totalAwarded;
      
      // Write merged state and scoped completion atomically
      batch.set(reflectionGameRef, currentState, { merge: true });
      batch.set(reflectionGameRef, {
        [scopedPath]: {
          submissionId,
          submittedAt: Timestamp.now()
        },
        // Optionally keep aggregate stats global for now
      } as any, { merge: true });
      
      // Mark campaigns as answered (merge to create if missing)
      for (const campaignId of submission.templateSnapshot.campaignIds) {
        const campaignStateRef = doc(db, 'users', studentId, 'campaignState', campaignId);
        batch.set(campaignStateRef, {
          answered: true,
          answeredAt: serverTimestamp()
        }, { merge: true });
      }
      
      console.log('[ProgressService.submitReflection] committing batch');
      await batch.commit();
      console.log('[ProgressService.submitReflection] success', { submissionId, totalAwarded });

      // Check for Season Pass node unlocks after successful level completion
      try {
        await SeasonPassService.checkAndUnlockNodes(
          studentId,
          levelId,
          DEFAULT_SEASON,
          { squadId, curriculumId }
        );
      } catch (error) {
        console.warn('Failed to check Season Pass node unlocks:', error);
        // Don't fail the entire submission for Season Pass errors
      }

      return { success: true, totalAwarded, submissionId };
    } catch (error) {
      console.error('Error submitting reflection:', error);
      return { success: false, totalAwarded: 0 };
    }
  }

  // Get student's reflection history
  static async getStudentSubmissions(studentId: string, limitCount = 10): Promise<StudentSubmission[]> {
    try {
      const submissionsRef = collection(db, 'studentFeedback');
      const q = query(
        submissionsRef,
        where('studentId', '==', studentId),
        orderBy('submittedAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as StudentSubmission));
    } catch (error) {
      console.error('Error getting student submissions:', error);
      return [];
    }
  }

  // Check if level is already completed
  static async isLevelCompleted(studentId: string, levelId: string): Promise<boolean> {
    try {
      // Ensure reflection state exists for this user before accessing it
      await this.ensureReflectionStateExists(studentId);

      const reflectionGameDoc = await getDoc(doc(db, 'users', studentId, 'reflectionGame', 'state'));
      if (!reflectionGameDoc.exists()) {
        console.warn('[ProgressService.isLevelCompleted] State still missing after ensure call', { studentId });
        return false;
      }

      const state = reflectionGameDoc.data() as ReflectionGameState;

      // Defensive null-safety: handle cases where completedLevels doesn't exist
      if (!state || typeof state !== 'object') {
        console.warn('[ProgressService.isLevelCompleted] Invalid state object', { studentId, state });
        return false;
      }

      const completedLevels = state.completedLevels;
      if (!completedLevels || typeof completedLevels !== 'object') {
        console.debug('[ProgressService.isLevelCompleted] No completedLevels found for user', { studentId, levelId });
        return false;
      }

      return !!completedLevels[levelId];
    } catch (error) {
      console.error('Error checking level completion:', error);
      return false;
    }
  }

  // Get student's reflection game stats
  static async getReflectionStats(studentId: string): Promise<ReflectionGameState | null> {
    try {
      // Ensure reflection state exists for this user before accessing it
      await this.ensureReflectionStateExists(studentId);

      const docSnap = await getDoc(doc(db, 'users', studentId, 'reflectionGame', 'state'));

      if (!docSnap.exists()) {
        console.warn('[ProgressService.getReflectionStats] State still missing after ensure call', { studentId });
        return null;
      }

      const rawData = docSnap.data();
      if (!rawData || typeof rawData !== 'object') {
        console.warn('[ProgressService.getReflectionStats] Invalid reflection state data', { studentId, rawData });
        return null;
      }

      // Return with defensive structure - ensure required fields exist
      const state = rawData as ReflectionGameState;

      // Validate and provide defaults for critical fields
      const safeState: ReflectionGameState = {
        completedLevels: state.completedLevels && typeof state.completedLevels === 'object'
          ? state.completedLevels
          : {},
        unlockedCount: typeof state.unlockedCount === 'number'
          ? state.unlockedCount
          : 0,
        stats: state.stats && typeof state.stats === 'object'
          ? {
              totalQuestionsAnswered: typeof state.stats.totalQuestionsAnswered === 'number'
                ? state.stats.totalQuestionsAnswered
                : 0,
              totalCashFromProgress: typeof state.stats.totalCashFromProgress === 'number'
                ? state.stats.totalCashFromProgress
                : 0
            }
          : {
              totalQuestionsAnswered: 0,
              totalCashFromProgress: 0
            }
      };

      return safeState;
    } catch (error) {
      console.error('Error getting reflection stats:', error);
      return null;
    }
  }
}